import { useState, useEffect } from 'react';
import {
  Calendar, MapPin, Clock, Users, Search, Filter,
  ChevronDown, Ticket, Star, X, ExternalLink,
  Music, Film, Utensils, BookOpen, Briefcase,
  Heart, Palette, PartyPopper, Plus, Check,
  CalendarPlus,
} from 'lucide-react';
import { Button, AdUnit } from '@/components/common';
import SEO from '@/components/common/SEO';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Event {
  id: string;
  title: string;
  title_ko?: string;
  description?: string;
  category: string;
  venue_name?: string;
  venue_address?: string;
  city: string;
  country: string;
  country_code?: string;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  price_min?: number;
  price_max?: number;
  currency: string;
  is_free: boolean;
  ticket_url?: string;
  website_url?: string;
  image_url?: string;
  organizer_name?: string;
  tags: string[];
  is_featured: boolean;
  attendee_count: number;
  max_attendees?: number;
  source?: 'kcc' | 'user';
}

interface CategoryInfo {
  key: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const CATEGORIES: CategoryInfo[] = [
  { key: 'all', label: 'All Events', icon: <Calendar className="w-4 h-4" />, color: 'bg-gray-500' },
  { key: 'cultural', label: 'Cultural', icon: <Palette className="w-4 h-4" />, color: 'bg-purple-500' },
  { key: 'performance', label: 'Performance', icon: <Music className="w-4 h-4" />, color: 'bg-pink-500' },
  { key: 'exhibition', label: 'Exhibition', icon: <Palette className="w-4 h-4" />, color: 'bg-indigo-500' },
  { key: 'film', label: 'Film', icon: <Film className="w-4 h-4" />, color: 'bg-red-500' },
  { key: 'workshop', label: 'Workshop', icon: <BookOpen className="w-4 h-4" />, color: 'bg-blue-500' },
  { key: 'food', label: 'Food & Drink', icon: <Utensils className="w-4 h-4" />, color: 'bg-orange-500' },
  { key: 'festival', label: 'Festival', icon: <PartyPopper className="w-4 h-4" />, color: 'bg-amber-500' },
  { key: 'social', label: 'Social', icon: <Heart className="w-4 h-4" />, color: 'bg-rose-500' },
  { key: 'business', label: 'Business', icon: <Briefcase className="w-4 h-4" />, color: 'bg-slate-600' },
];

const COUNTRIES = [
  { code: 'US', name: 'United States', cities: ['Los Angeles', 'New York', 'Chicago', 'San Francisco'] },
  { code: 'CA', name: 'Canada', cities: ['Toronto', 'Vancouver', 'Calgary', 'Montreal'] },
  { code: 'GB', name: 'United Kingdom', cities: ['London', 'Manchester', 'Birmingham', 'Edinburgh'] },
  { code: 'AU', name: 'Australia', cities: ['Sydney', 'Melbourne', 'Brisbane', 'Perth'] },
  { code: 'DE', name: 'Germany', cities: ['Berlin', 'Frankfurt', 'Munich', 'Düsseldorf'] },
  { code: 'FR', name: 'France', cities: ['Paris', 'Lyon', 'Marseille', 'Toulouse'] },
  { code: 'JP', name: 'Japan', cities: ['Tokyo', 'Osaka', 'Nagoya', 'Fukuoka'] },
  { code: 'KR', name: 'South Korea', cities: ['Seoul', 'Busan', 'Incheon', 'Daegu'] },
  { code: 'SG', name: 'Singapore', cities: ['Singapore'] },
];

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  kcc: { label: 'Korean Cultural Center', color: 'bg-blue-600' },
  user: { label: 'Community', color: 'bg-green-500' },
};

export default function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [featuredEvents, setFeaturedEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFreeOnly, setShowFreeOnly] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // Get cities for selected country
  const availableCities = selectedCountry
    ? COUNTRIES.find(c => c.code === selectedCountry)?.cities || []
    : [];

  // Reset city when country changes
  useEffect(() => {
    setSelectedCity('');
  }, [selectedCountry]);

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedCategory !== 'all') params.append('category', selectedCategory);
        if (selectedCountry) params.append('country', selectedCountry);
        if (selectedCity) params.append('city', selectedCity);
        if (showFreeOnly) params.append('free', 'true');
        if (searchQuery) params.append('search', searchQuery);

        const response = await fetch(`${API_URL}/api/events?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          setEvents(data.events || []);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [selectedCategory, selectedCountry, selectedCity, showFreeOnly, searchQuery]);

  // Fetch featured events
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await fetch(`${API_URL}/api/events/featured`);
        if (response.ok) {
          const data = await response.json();
          setFeaturedEvents(data || []);
        }
      } catch (error) {
        console.error('Error fetching featured events:', error);
      }
    };

    fetchFeatured();
  }, []);

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format time
  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Format price
  const formatPrice = (event: Event) => {
    if (event.is_free) return 'Free';
    if (!event.price_min) return 'Price TBA';
    if (event.price_min === event.price_max || !event.price_max) {
      return `$${event.price_min}`;
    }
    return `$${event.price_min} - $${event.price_max}`;
  };

  // Get category info
  const getCategoryInfo = (categoryKey: string): CategoryInfo => {
    return CATEGORIES.find(c => c.key === categoryKey) || CATEGORIES[0];
  };

  // Get category gradient for card
  const getCategoryGradient = (category: string): string => {
    const gradients: Record<string, string> = {
      cultural: 'from-purple-500 to-indigo-600',
      performance: 'from-pink-500 to-rose-600',
      exhibition: 'from-indigo-500 to-purple-600',
      film: 'from-red-500 to-rose-600',
      workshop: 'from-blue-500 to-cyan-600',
      food: 'from-orange-500 to-amber-600',
      festival: 'from-amber-500 to-yellow-600',
      social: 'from-rose-500 to-pink-600',
      business: 'from-slate-600 to-gray-700',
      language: 'from-cyan-500 to-blue-600',
      lecture: 'from-teal-500 to-emerald-600',
    };
    return gradients[category] || 'from-gray-500 to-slate-600';
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      <SEO
        title="Korean Community Events & Meetups"
        description="Find Korean community events, meetups, and gatherings near you. Connect with other Korean expats in your city."
        path="/events"
      />
      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Events
          </h1>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
            Discover Korean cultural events, festivals, and community gatherings
          </p>
        </div>
        <Button
          onClick={() => setShowSubmitModal(true)}
          className="bg-coral-500 hover:bg-coral-600 flex items-center gap-2 w-full sm:w-auto justify-center"
        >
          <Plus className="w-4 h-4" />
          Submit Event
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-coral-500"
            />
          </div>

          {/* Country Filter */}
          <div className="relative">
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="appearance-none pl-4 pr-10 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500 cursor-pointer"
            >
              <option value="">All Countries</option>
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* City Filter (shows when country selected) */}
          {selectedCountry && availableCities.length > 0 && (
            <div className="relative">
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="appearance-none pl-4 pr-10 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500 cursor-pointer"
              >
                <option value="">All Cities</option>
                {availableCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          )}

          {/* Free Events Toggle */}
          <button
            onClick={() => setShowFreeOnly(!showFreeOnly)}
            className={`px-4 py-3 rounded-xl font-medium transition-colors ${
              showFreeOnly
                ? 'bg-green-500 text-white'
                : 'bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600'
            }`}
          >
            Free Events
          </button>

          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden px-4 py-3 bg-gray-50 dark:bg-slate-700 rounded-xl text-gray-700 dark:text-gray-300"
          >
            <Filter className="w-5 h-5" />
          </button>
        </div>

        {/* Category Pills */}
        <div className={`mt-4 flex flex-wrap gap-2 ${showFilters ? '' : 'hidden md:flex'}`}>
          {CATEGORIES.map((category) => (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === category.key
                  ? `${category.color} text-white shadow-md`
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              {category.icon}
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Featured Events */}
      {featuredEvents.length > 0 && selectedCategory === 'all' && !searchQuery && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Featured Events
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {featuredEvents.slice(0, 3).map((event) => (
              <div
                key={event.id}
                onClick={() => setSelectedEvent(event)}
                className="group relative rounded-2xl overflow-hidden cursor-pointer shadow-lg hover:shadow-xl transition-all"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${getCategoryGradient(event.category)}`} />
                <div className="relative p-6 text-white">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">
                      {getCategoryInfo(event.category).label}
                    </span>
                    {event.is_free && (
                      <span className="px-2 py-1 bg-green-500/80 rounded-full text-xs font-medium">
                        Free
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold mb-2 group-hover:underline">
                    {event.title}
                  </h3>
                  <p className="text-sm text-white/80 flex items-center gap-1 mb-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(event.start_date)}
                    {event.start_time && ` at ${formatTime(event.start_time)}`}
                  </p>
                  <p className="text-sm text-white/80 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {event.city}, {event.country}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ad Banner */}
      <AdUnit slot="6789012345" format="horizontal" className="mb-6" />

      {/* Events Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden animate-pulse">
              <div className="h-32 bg-gray-200 dark:bg-slate-700" />
              <div className="p-4">
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Events Found
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Try adjusting your filters or check back later for new events.
          </p>
          <Button
            onClick={() => setShowSubmitModal(true)}
            className="bg-coral-500 hover:bg-coral-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Submit Your Event
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onClick={() => setSelectedEvent(event)}
              formatDate={formatDate}
              formatTime={formatTime}
              formatPrice={formatPrice}
              getCategoryInfo={getCategoryInfo}
            />
          ))}
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          formatDate={formatDate}
          formatTime={formatTime}
          formatPrice={formatPrice}
          getCategoryInfo={getCategoryInfo}
          getCategoryGradient={getCategoryGradient}
        />
      )}

      {/* Submit Event Modal */}
      {showSubmitModal && (
        <SubmitEventModal
          onClose={() => setShowSubmitModal(false)}
          onSuccess={() => {
            setShowSubmitModal(false);
            // Optionally refresh events list
          }}
        />
      )}
    </div>
  );
}

// Event Card Component
interface EventCardProps {
  event: Event;
  onClick: () => void;
  formatDate: (date: string) => string;
  formatTime: (time?: string) => string;
  formatPrice: (event: Event) => string;
  getCategoryInfo: (category: string) => CategoryInfo;
}

function EventCard({ event, onClick, formatDate, formatTime, formatPrice, getCategoryInfo }: EventCardProps) {
  const category = getCategoryInfo(event.category);

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all group"
    >
      {/* Image/Header */}
      <div className="relative h-32">
        {event.image_url ? (
          <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${
            event.category === 'cultural' ? 'from-purple-400 to-indigo-500' :
            event.category === 'performance' ? 'from-pink-400 to-rose-500' :
            event.category === 'exhibition' ? 'from-indigo-400 to-purple-500' :
            event.category === 'film' ? 'from-red-400 to-rose-500' :
            event.category === 'workshop' ? 'from-blue-400 to-cyan-500' :
            event.category === 'food' ? 'from-orange-400 to-amber-500' :
            event.category === 'festival' ? 'from-amber-400 to-yellow-500' :
            event.category === 'social' ? 'from-rose-400 to-pink-500' :
            event.category === 'business' ? 'from-slate-500 to-gray-600' :
            'from-teal-400 to-emerald-500'
          } flex items-center justify-center`}>
            <div className="text-white/30">
              {category.icon && <span className="scale-[3] block">{category.icon}</span>}
            </div>
          </div>
        )}

        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 ${category.color} text-white rounded-full text-xs font-medium flex items-center gap-1`}>
            {category.icon}
            {category.label}
          </span>
        </div>

        {/* Price Badge */}
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            event.is_free
              ? 'bg-green-500 text-white'
              : 'bg-white/90 dark:bg-slate-800/90 text-gray-900 dark:text-white'
          }`}>
            {formatPrice(event)}
          </span>
        </div>

        {/* Featured Badge */}
        {event.is_featured && (
          <div className="absolute bottom-3 left-3">
            <span className="px-2 py-1 bg-amber-500 text-white rounded-full text-xs font-medium flex items-center gap-1">
              <Star className="w-3 h-3" />
              Featured
            </span>
          </div>
        )}

        {/* Source Badge */}
        {event.source && (
          <div className="absolute bottom-3 right-3">
            <span className={`px-2 py-1 ${SOURCE_LABELS[event.source]?.color || 'bg-gray-500'} text-white rounded-full text-xs font-medium`}>
              {SOURCE_LABELS[event.source]?.label || event.source}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-coral-500 transition-colors line-clamp-2">
          {event.title}
        </h3>

        <div className="space-y-1 text-sm text-gray-500 dark:text-gray-400">
          <p className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-coral-500" />
            {formatDate(event.start_date)}
            {event.end_date && event.end_date !== event.start_date && (
              <span className="text-xs">- {formatDate(event.end_date)}</span>
            )}
          </p>

          {event.start_time && (
            <p className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-coral-500" />
              {formatTime(event.start_time)}
              {event.end_time && ` - ${formatTime(event.end_time)}`}
            </p>
          )}

          <p className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-coral-500" />
            {event.venue_name || event.city}, {event.country}
          </p>

          {event.max_attendees && (
            <p className="flex items-center gap-2">
              <Users className="w-4 h-4 text-coral-500" />
              {event.attendee_count || 0} / {event.max_attendees} attending
            </p>
          )}
        </div>

        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {event.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 rounded text-xs"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Event Detail Modal
interface EventDetailModalProps {
  event: Event;
  onClose: () => void;
  formatDate: (date: string) => string;
  formatTime: (time?: string) => string;
  formatPrice: (event: Event) => string;
  getCategoryInfo: (category: string) => CategoryInfo;
  getCategoryGradient: (category: string) => string;
}

function generateGoogleCalendarUrl(event: Event): string {
  const startDate = event.start_date.replace(/-/g, '');
  const endDate = (event.end_date || event.start_date).replace(/-/g, '');
  const startTime = event.start_time ? event.start_time.replace(/:/g, '') + '00' : '';
  const endTime = event.end_time ? event.end_time.replace(/:/g, '') + '00' : '';

  // If times are provided, use datetime format; otherwise use all-day format
  const dates = startTime
    ? `${startDate}T${startTime}/${endDate}T${endTime || startTime}`
    : `${startDate}/${endDate}`;

  const location = [event.venue_name, event.venue_address, `${event.city}, ${event.country}`]
    .filter(Boolean)
    .join(', ');

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates,
    location,
    details: event.description || '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function generateIcsContent(event: Event): string {
  const formatIcsDate = (date: string, time?: string) => {
    const d = date.replace(/-/g, '');
    if (time) {
      return `${d}T${time.replace(/:/g, '')}00`;
    }
    return d;
  };

  const start = formatIcsDate(event.start_date, event.start_time);
  const end = formatIcsDate(event.end_date || event.start_date, event.end_time || event.start_time);
  const location = [event.venue_name, event.venue_address, `${event.city}, ${event.country}`]
    .filter(Boolean)
    .join(', ');

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//KoreanHub//Events//EN',
    'BEGIN:VEVENT',
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${event.title}`,
    `LOCATION:${location}`,
    `DESCRIPTION:${(event.description || '').replace(/\n/g, '\\n')}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.join('\r\n');
}

function downloadIcsFile(event: Event) {
  const content = generateIcsContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function EventDetailModal({
  event,
  onClose,
  formatDate,
  formatTime,
  formatPrice,
  getCategoryInfo,
  getCategoryGradient,
}: EventDetailModalProps) {
  const category = getCategoryInfo(event.category);
  const [showCalendarMenu, setShowCalendarMenu] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Image */}
        <div className={`relative h-48 bg-gradient-to-br ${getCategoryGradient(event.category)}`}>
          {event.image_url && (
            <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          )}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Badges */}
          <div className="absolute bottom-4 left-4 flex gap-2">
            <span className={`px-3 py-1 ${category.color} text-white rounded-full text-sm font-medium flex items-center gap-1`}>
              {category.icon}
              {category.label}
            </span>
            {event.is_featured && (
              <span className="px-3 py-1 bg-amber-500 text-white rounded-full text-sm font-medium flex items-center gap-1">
                <Star className="w-4 h-4" />
                Featured
              </span>
            )}
            {event.source && (
              <span className={`px-3 py-1 ${SOURCE_LABELS[event.source]?.color || 'bg-gray-500'} text-white rounded-full text-sm font-medium`}>
                {SOURCE_LABELS[event.source]?.label}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {event.title}
              </h2>
              {event.title_ko && (
                <p className="text-gray-500 dark:text-gray-400">{event.title_ko}</p>
              )}
            </div>
            <div className={`px-4 py-2 rounded-xl text-lg font-bold ${
              event.is_free
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-coral-100 text-coral-700 dark:bg-coral-900/30 dark:text-coral-400'
            }`}>
              {formatPrice(event)}
            </div>
          </div>

          {/* Event Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Date & Time</h3>
              <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <Calendar className="w-4 h-4 text-coral-500" />
                {formatDate(event.start_date)}
                {event.end_date && event.end_date !== event.start_date && (
                  <span className="text-gray-500">to {formatDate(event.end_date)}</span>
                )}
              </p>
              {event.start_time && (
                <p className="text-gray-600 dark:text-gray-300 flex items-center gap-2 mt-1">
                  <Clock className="w-4 h-4 text-coral-500" />
                  {formatTime(event.start_time)}
                  {event.end_time && ` - ${formatTime(event.end_time)}`}
                </p>
              )}
            </div>

            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Location</h3>
              <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <MapPin className="w-4 h-4 text-coral-500" />
                {event.venue_name || event.city}
              </p>
              {event.venue_address && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{event.venue_address}</p>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {event.city}, {event.country}
              </p>
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">About</h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                {event.description}
              </p>
            </div>
          )}

          {/* Organizer */}
          {event.organizer_name && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Organizer</h3>
              <p className="font-medium text-gray-900 dark:text-white">{event.organizer_name}</p>
            </div>
          )}

          {/* Capacity */}
          {event.max_attendees && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Capacity</h3>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-coral-500" />
                <span className="text-gray-900 dark:text-white">
                  {event.attendee_count || 0} / {event.max_attendees} attending
                </span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-coral-500 rounded-full"
                  style={{ width: `${Math.min(100, ((event.attendee_count || 0) / event.max_attendees) * 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Tags */}
          {event.tags && event.tags.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t dark:border-slate-700">
            {event.ticket_url && (
              <Button
                onClick={() => window.open(event.ticket_url, '_blank')}
                className="flex-1 bg-coral-500 hover:bg-coral-600"
              >
                <Ticket className="w-4 h-4 mr-2" />
                Get Tickets
              </Button>
            )}
            {event.website_url && (
              <Button
                onClick={() => window.open(event.website_url, '_blank')}
                variant="outline"
                className="flex-1"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Website
              </Button>
            )}
            {!event.ticket_url && !event.website_url && (
              <Button
                disabled
                className="flex-1 cursor-not-allowed bg-gray-200 text-gray-500 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-400"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Registration link unavailable
              </Button>
            )}

            {/* Add to Calendar dropdown */}
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowCalendarMenu(!showCalendarMenu)}
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                Add to Calendar
                <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showCalendarMenu ? 'rotate-180' : ''}`} />
              </Button>
              {showCalendarMenu && (
                <div className="absolute bottom-full mb-2 right-0 bg-white dark:bg-slate-700 rounded-xl shadow-lg border border-gray-200 dark:border-slate-600 py-1 z-10 min-w-[200px]">
                  <a
                    href={generateGoogleCalendarUrl(event)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                    onClick={() => setShowCalendarMenu(false)}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.5 3h-1V1.5h-2V3h-9V1.5h-2V3h-1A2.5 2.5 0 0 0 2 5.5v14A2.5 2.5 0 0 0 4.5 22h15a2.5 2.5 0 0 0 2.5-2.5v-14A2.5 2.5 0 0 0 19.5 3zm0 17h-15V8h15v12z" />
                    </svg>
                    Google Calendar
                  </a>
                  <button
                    onClick={() => {
                      downloadIcsFile(event);
                      setShowCalendarMenu(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors w-full"
                  >
                    <Calendar className="w-4 h-4" />
                    Apple Calendar (.ics)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Submit Event Modal
interface SubmitEventModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function SubmitEventModal({ onClose, onSuccess }: SubmitEventModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    title_ko: '',
    description: '',
    category: 'cultural',
    venue_name: '',
    venue_address: '',
    country: 'United States',
    city: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    is_free: false,
    price_min: '',
    price_max: '',
    ticket_url: '',
    website_url: '',
    image_url: '',
    organizer_name: '',
    organizer_email: '',
    max_attendees: '',
    tags: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const selectedCountryData = COUNTRIES.find(c => c.name === formData.country);
  const availableCities = selectedCountryData?.cities || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          price_min: formData.price_min ? parseFloat(formData.price_min) : null,
          price_max: formData.price_max ? parseFloat(formData.price_max) : null,
          max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
          tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to submit event');
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-md w-full text-center" onClick={e => e.stopPropagation()}>
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Event Submitted!</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Thank you for submitting your event. It will be reviewed and published soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Submit an Event</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Share your Korean cultural event with the community</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Event Details</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Event Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                placeholder="e.g., Korean Film Festival 2026"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title in Korean (optional)
              </label>
              <input
                type="text"
                value={formData.title_ko}
                onChange={(e) => setFormData({ ...formData, title_ko: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                placeholder="e.g., 한국영화제 2026"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
              >
                {CATEGORIES.filter(c => c.key !== 'all').map((cat) => (
                  <option key={cat.key} value={cat.key}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                placeholder="Describe your event..."
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Location</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Country *
                </label>
                <select
                  required
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value, city: '' })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                >
                  {COUNTRIES.map((country) => (
                    <option key={country.code} value={country.name}>{country.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City *
                </label>
                <select
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                >
                  <option value="">Select City</option>
                  {availableCities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Venue Name
              </label>
              <input
                type="text"
                value={formData.venue_name}
                onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                placeholder="e.g., Korean Cultural Center"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Venue Address
              </label>
              <input
                type="text"
                value={formData.venue_address}
                onChange={(e) => setFormData({ ...formData, venue_address: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                placeholder="e.g., 5505 Wilshire Blvd"
              />
            </div>
          </div>

          {/* Date & Time */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Date & Time</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Pricing</h3>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_free"
                checked={formData.is_free}
                onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-coral-500 focus:ring-coral-500"
              />
              <label htmlFor="is_free" className="text-gray-700 dark:text-gray-300">
                This is a free event
              </label>
            </div>

            {!formData.is_free && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Min Price ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price_min}
                    onChange={(e) => setFormData({ ...formData, price_min: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Price ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price_max}
                    onChange={(e) => setFormData({ ...formData, price_max: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Links</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Ticket URL
              </label>
              <input
                type="url"
                value={formData.ticket_url}
                onChange={(e) => setFormData({ ...formData, ticket_url: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Website URL
              </label>
              <input
                type="url"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Image URL
              </label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Organizer */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Organizer Info</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organizer Name
                </label>
                <input
                  type="text"
                  value={formData.organizer_name}
                  onChange={(e) => setFormData({ ...formData, organizer_name: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                  placeholder="Your name or organization"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.organizer_email}
                  onChange={(e) => setFormData({ ...formData, organizer_email: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                  placeholder="email@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Attendees (optional)
              </label>
              <input
                type="number"
                min="1"
                value={formData.max_attendees}
                onChange={(e) => setFormData({ ...formData, max_attendees: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                placeholder="Leave empty for unlimited"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tags (comma separated)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500"
              placeholder="e.g., k-pop, traditional, family-friendly"
            />
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4 border-t dark:border-slate-700">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-coral-500 hover:bg-coral-600"
            >
              {submitting ? 'Submitting...' : 'Submit Event'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
